import type React from 'react'
import { forwardRef, useState, type InputHTMLAttributes, type TextareaHTMLAttributes } from 'react'

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={['input', className ?? ''].filter(Boolean).join(' ')} {...rest} />
  },
)

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...rest }, ref) {
    return <textarea ref={ref} className={['input', className ?? ''].filter(Boolean).join(' ')} {...rest} />
  },
)

/**
 * For API keys and other secrets an admin pastes in — NOT for a person's own
 * password. A `type="password"` field makes Safari/iCloud Keychain, 1Password,
 * Bitwarden etc. treat the form as a login and offer to fill or save it (and
 * the field above it as a "username"). This is a plain text input masked with
 * CSS, with every password manager's opt-out attribute and a Show/Hide toggle.
 */
export const SecretInput = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>>(
  function SecretInput({ className, style, ...rest }, ref) {
    const [shown, setShown] = useState(false)
    return (
      <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'center' }}>
        <input
          ref={ref}
          type="text"
          className={['input', className ?? ''].filter(Boolean).join(' ')}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          data-1p-ignore=""
          data-lpignore="true"
          data-bwignore=""
          data-form-type="other"
          style={{ flex: 1, minWidth: 0, ...(shown ? {} : { WebkitTextSecurity: 'disc' }), ...style } as React.CSSProperties}
          {...rest}
        />
        <button
          type="button"
          className="btn btn-ghost"
          style={{ flex: 'none', fontSize: 12 }}
          aria-pressed={shown}
          onClick={() => setShown((value) => !value)}
        >
          {shown ? 'Hide' : 'Show'}
        </button>
      </div>
    )
  },
)

/** Attributes that keep password managers off an ordinary text field next to a secret (e.g. a public site key). */
export const NO_AUTOFILL = {
  autoComplete: 'off',
  'data-1p-ignore': '',
  'data-lpignore': 'true',
  'data-bwignore': '',
  'data-form-type': 'other',
} as const
